import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { Avatar } from "@/components/common/Avatar";
import { SearchInput } from "@/components/common/SearchInput";
import { Button } from "@/components/ui/button";
import { dateFmt } from "@/lib/format";
import { Award, Printer, X } from "lucide-react";

function Certificate({ winner, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6">
      <div className="no-print absolute right-6 top-6 flex gap-2">
        <Button variant="outline" onClick={() => window.print()}><Printer className="mr-1.5 h-4 w-4" />Print / Export PDF</Button>
        <Button variant="outline" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
      </div>
      <div className="print-area max-h-full w-full max-w-2xl overflow-auto rounded-2xl border-8 border-double border-primary/40 bg-white p-12 text-center shadow-2xl">
        <Award className="mx-auto h-14 w-14 text-primary" />
        <div className="mt-4 font-display text-xs uppercase tracking-[0.3em] text-muted-foreground">Certificate of Recognition</div>
        <div className="mt-2 font-display text-3xl font-bold text-foreground">Employee of the Month</div>
        <div className="mt-8 font-display text-2xl font-semibold text-primary">{winner.employee_name}</div>
        <p className="mx-auto mt-4 max-w-md text-sm text-muted-foreground">{winner.reason}</p>
        <div className="mt-8 text-xs uppercase tracking-wide text-muted-foreground">{winner.month_label}</div>
        <div className="mt-1 text-xs text-muted-foreground">Awarded {dateFmt(winner.created_at)}</div>
      </div>
    </div>
  );
}

export default function EmployeeOfMonth() {
  const [certFor, setCertFor] = useState(null);
  const [search, setSearch] = useState("");
  const { data: current = [], isLoading: lc } = useQuery({ queryKey: ["eotm-current"], queryFn: () => get("/hrms/eotm/current") });
  const { data: all = [], isLoading: la } = useQuery({ queryKey: ["eotm-all"], queryFn: () => get("/hrms/eotm") });
  const past = useMemo(() => {
    const currentIds = new Set(current.map((c) => c.id));
    return all.filter((w) => !currentIds.has(w.id));
  }, [all, current]);
  const filteredPast = useMemo(() => past.filter((w) => !search || w.employee_name?.toLowerCase().includes(search.toLowerCase())), [past, search]);

  return (
    <div>
      <PageHeader title="Employee of the Month" subtitle="Recognition auto-selected from each closed review cycle's top performer(s)." />

      {lc ? <Skeleton className="h-40" /> : !current.length ? (
        <EmptyState icon={Award} title="No current winner" description="Close a performance review cycle to auto-select the top scorer." />
      ) : (
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {current.map((w) => (
            <div key={w.id} data-testid={`eotm-current-${w.employee_id}`} className="rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-transparent p-6">
              <div className="flex items-center gap-3">
                <Avatar user={{ name: w.employee_name }} size="lg" />
                <div>
                  <div className="text-xs uppercase tracking-wide text-primary">Current Winner · {w.month_label}</div>
                  <div className="font-display text-lg font-bold">{w.employee_name}</div>
                </div>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">{w.reason}</p>
              <Button size="sm" className="mt-4" data-testid={`view-certificate-${w.employee_id}`} onClick={() => setCertFor(w)}>View Certificate</Button>
            </div>
          ))}
        </div>
      )}

      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-sm font-semibold text-muted-foreground">Past Winners</h3>
        {past.length > 0 && <SearchInput testid="eotm-search" placeholder="Search employee" value={search} onChange={setSearch} className="w-56" />}
      </div>
      {la ? <Skeleton className="h-48" /> : !past.length ? (
        <p className="text-sm text-muted-foreground">No past winners yet.</p>
      ) : !filteredPast.length ? (
        <p className="text-sm text-muted-foreground">No winners match your search.</p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-semibold">Employee</th><th className="px-4 py-3 font-semibold">Cycle</th>
              <th className="px-4 py-3 font-semibold">Score</th><th className="px-4 py-3 font-semibold">Date</th><th className="px-4 py-3"></th></tr></thead>
            <tbody>
              {filteredPast.map((w) => (
                <tr key={w.id} className="border-b border-border last:border-0 hover:bg-muted/50">
                  <td className="px-4 py-3"><div className="flex items-center gap-2"><Avatar user={{ name: w.employee_name }} size="sm" /><span className="font-medium">{w.employee_name}</span></div></td>
                  <td className="px-4 py-3 text-muted-foreground">{w.month_label}</td>
                  <td className="px-4 py-3">{w.score}%</td>
                  <td className="px-4 py-3 text-muted-foreground">{dateFmt(w.created_at)}</td>
                  <td className="px-4 py-3 text-right"><Button size="sm" variant="ghost" onClick={() => setCertFor(w)}>Certificate</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {certFor && <Certificate winner={certFor} onClose={() => setCertFor(null)} />}
    </div>
  );
}
